import { authenticatedHttpClient } from './httpClient';
import {
  AccountsReceivableQueryResult,
  AgingReport,
  ClientCreditQueryResult,
  ClientReceivableGroupsQueryResult,
  ClientStatement,
  CustomerBalanceSummary,
  ReceivableAdjustmentCreate,
  ReceivableCancelRequest,
  ReceivableMovementResult,
  ReceivablePaymentCreate,
} from '../models/accountsReceivable';

export interface AccountsReceivableFilters {
  clientId?: string;
  status?: string;
  agingBucket?: string;
  overdueOnly?: boolean;
  relevantOnly?: boolean;
  search?: string;
  viewMode?: 'operativo' | 'contable';
  limit?: number;
  offset?: number;
}

export const listAccountsReceivable = async (
  organizationId: string,
  filters?: AccountsReceivableFilters
): Promise<AccountsReceivableQueryResult> => {
  const params = new URLSearchParams();
  if (filters?.clientId) params.append('client_id', filters.clientId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.agingBucket) params.append('aging_bucket', filters.agingBucket);
  if (filters?.overdueOnly) params.append('overdue_only', 'true');
  if (typeof filters?.relevantOnly === 'boolean') {
    params.append('relevant_only', filters.relevantOnly ? 'true' : 'false');
  }
  if (filters?.search?.trim()) {
    params.append('search', filters.search.trim());
  }
  if (filters?.viewMode) {
    params.append('view_mode', filters.viewMode);
  }
  if (typeof filters?.limit === 'number' && filters.limit > 0) {
    params.append('limit', String(filters.limit));
  }
  if (typeof filters?.offset === 'number' && filters.offset >= 0) {
    params.append('offset', String(filters.offset));
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return authenticatedHttpClient.get<AccountsReceivableQueryResult>(
    `/accounts-receivable/${organizationId}${suffix}`
  );
};

export const listClientReceivableGroups = async (
  organizationId: string,
  filters?: AccountsReceivableFilters & { viewMode?: 'operativo' | 'contable' }
): Promise<ClientReceivableGroupsQueryResult> => {
  const params = new URLSearchParams();
  if (filters?.status) params.append('status', filters.status);
  if (filters?.agingBucket) params.append('aging_bucket', filters.agingBucket);
  if (filters?.overdueOnly) params.append('overdue_only', 'true');
  if (typeof filters?.relevantOnly === 'boolean') {
    params.append('relevant_only', filters.relevantOnly ? 'true' : 'false');
  }
  if (filters?.search?.trim()) {
    params.append('search', filters.search.trim());
  }
  if (filters?.viewMode) {
    params.append('view_mode', filters.viewMode);
  }
  if (typeof filters?.limit === 'number' && filters.limit > 0) {
    params.append('limit', String(filters.limit));
  }
  if (typeof filters?.offset === 'number' && filters.offset >= 0) {
    params.append('offset', String(filters.offset));
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return authenticatedHttpClient.get<ClientReceivableGroupsQueryResult>(
    `/accounts-receivable/${organizationId}/client-groups${suffix}`
  );
};

export const createReceivablePayment = async (
  organizationId: string,
  receivableId: string,
  payment: ReceivablePaymentCreate
): Promise<ReceivableMovementResult> => {
  return authenticatedHttpClient.post<ReceivableMovementResult>(
    `/accounts-receivable/${organizationId}/${receivableId}/payments`,
    { payment }
  );
};

export const createReceivableAdjustment = async (
  organizationId: string,
  receivableId: string,
  adjustment: ReceivableAdjustmentCreate
): Promise<ReceivableMovementResult> => {
  return authenticatedHttpClient.post<ReceivableMovementResult>(
    `/accounts-receivable/${organizationId}/${receivableId}/adjustments`,
    { adjustment }
  );
};

export const cancelReceivable = async (
  organizationId: string,
  receivableId: string,
  cancellation: ReceivableCancelRequest
): Promise<{ ok: boolean }> => {
  return authenticatedHttpClient.post<{ ok: boolean }>(
    `/accounts-receivable/${organizationId}/${receivableId}/cancel`,
    { cancellation }
  );
};

export const getAgingReport = async (
  organizationId: string,
  clientId?: string
): Promise<AgingReport> => {
  const params = new URLSearchParams();
  if (clientId) params.append('client_id', clientId);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return authenticatedHttpClient.get<AgingReport>(
    `/accounts-receivable/${organizationId}/reports/aging${suffix}`
  );
};

export const getClientStatement = async (
  organizationId: string,
  clientId: string
): Promise<ClientStatement> => {
  return authenticatedHttpClient.get<ClientStatement>(
    `/accounts-receivable/${organizationId}/reports/client-statement/${clientId}`
  );
};

export const listClientCredits = async (
  organizationId: string,
  filters?: { clientId?: string; status?: string }
): Promise<ClientCreditQueryResult> => {
  const params = new URLSearchParams();
  if (filters?.clientId) params.append('client_id', filters.clientId);
  if (filters?.status) params.append('status', filters.status);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return authenticatedHttpClient.get<ClientCreditQueryResult>(
    `/accounts-receivable/${organizationId}/client-credits${suffix}`
  );
};

export const getCustomerBalanceSummary = async (
  organizationId: string,
  filters?: AccountsReceivableFilters & { clientId?: string }
): Promise<CustomerBalanceSummary> => {
  const params = new URLSearchParams();
  if (filters?.clientId) params.append('client_id', filters.clientId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.agingBucket) params.append('aging_bucket', filters.agingBucket);
  if (filters?.overdueOnly) params.append('overdue_only', 'true');
  if (typeof filters?.relevantOnly === 'boolean') {
    params.append('relevant_only', filters.relevantOnly ? 'true' : 'false');
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  return authenticatedHttpClient.get<CustomerBalanceSummary>(
    `/accounts-receivable/${organizationId}/reports/customer-balance${suffix}`
  );
};
